import React, { useState, useEffect, useCallback } from 'react';
import { useSecurity } from './contexts/SecurityContext';
import './style/PageStyle/RegisteredDevice.css'; 
import { API_BASE_URL } from './services/api/config'; 

const RegisteredDevice = () => {
  const { secureFetch } = useSecurity();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Editing State
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  // --- Fetch Devices ---
  const fetchDevices = useCallback(async () => {
    try {
      const response = await secureFetch(`${API_BASE_URL}/devices`);
      if (response.ok) {
        const data = await response.json();
        setDevices(data);
      } else {
        console.error("Failed to fetch devices");
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // --- Handlers ---

  const handleEditClick = (device) => {
    setEditingId(device.id);
    setEditName(device.device_name || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveEdit = async (id) => {
    if (!editName.trim()) return alert("Device name cannot be empty");

    try {
      const response = await secureFetch(`${API_BASE_URL}/devices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_name: editName })
      });

      if (response.ok) {
        // Optimistic UI update
        setDevices(devices.map(d => d.id === id ? { ...d, device_name: editName } : d));
        setEditingId(null);
      } else {
        alert("Failed to update device name");
      }
    } catch (error) {
      console.error("Update error:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to revoke this key? The user will no longer be able to use it to Operate Document.")) {
      return;
    }

    try {
      const response = await secureFetch(`${API_BASE_URL}/devices/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setDevices(devices.filter(d => d.id !== id));
      } else {
        alert("Failed to delete device");
      }
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  // --- Render Helpers ---
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', 
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="device-page-container">
      <div className="device-header">
        <h2>Registered Devices</h2>
        <p>View and manage WebAuthn security keys registered by users.</p>
      </div>

      <div className="device-card">
        {loading ? (
          <div className="loading-state">Loading registered devices...</div>
        ) : (
          <table className="device-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Device Name</th>
                <th>Registered</th>
                <th>Last Used</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.length > 0 ? (
                devices.map((device) => (
                  <tr key={device.id}>
                    <td className="col-user">{device.username}</td>
                    
                    <td>
                      {editingId === device.id ? (
                        <input 
                          type="text" 
                          className="edit-input"
                          value={editName} 
                          onChange={(e) => setEditName(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <span>{device.device_name || 'Unnamed Device'}</span>
                      )}
                    </td>

                    <td className="col-date">{formatDate(device.created_at)}</td>
                    <td className="col-date">{formatDate(device.last_used_at)}</td>

                    <td>
                      <div className="action-buttons">
                        {editingId === device.id ? (
                          <>
                            <button className="btn btn-save" onClick={() => handleSaveEdit(device.id)}>Save</button>
                            <button className="btn btn-cancel" onClick={handleCancelEdit}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-edit" onClick={() => handleEditClick(device)}>Edit</button>
                            <button className="btn btn-revoke" onClick={() => handleDelete(device.id)}>Revoke</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="empty-state">No devices found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RegisteredDevice;
